/**
 * WHAT TO ASK A RETAILER FOR (spec §61K).
 *
 * The Design already knows the answer: a 30" sink base, a 48" vanity. Asking
 * the provider for "bathroom vanity" and making the user narrow it down by
 * hand throws that away.
 *
 * These are the structured attributes the sourcing boundary understands.
 * Nothing is invented: an attribute nobody has stated is simply absent, and
 * an unknown attribute never becomes a guess that over-narrows the search.
 */
import { PU_PER_INCH } from "../geometry/units";
import { cabinetClassOf, cornerShapeOf, type CabinetClass } from "./objectConfig";

export interface SearchAttribute {
  key: string;
  value: string;
}

export interface SearchableObject {
  definitionId: string;
  /** Plan units. */
  width: number;
  heightInches: number | null;
  config: string | null;
}

/** Whole inches when it is (near enough) a whole number of them. */
function widthLabel(widthPu: number): string | null {
  const inches = widthPu / PU_PER_INCH;
  const rounded = Math.round(inches);
  if (rounded <= 0) return null;
  return Math.abs(inches - rounded) <= 0.2 ? `${rounded} in` : null;
}

const CABINET_TYPE: Readonly<Record<string, string>> = {
  "obj-base-cabinet": "base cabinet",
  "obj-drawer-base": "drawer base cabinet",
  "obj-sink-base": "sink base cabinet",
  "obj-wall-cabinet": "wall cabinet",
  "obj-tall-cabinet": "pantry cabinet",
  "obj-corner-base": "corner base cabinet",
  "obj-corner-wall": "corner wall cabinet",
  "obj-bath-cabinet": "bathroom storage cabinet",
  "obj-vanity": "bathroom vanity",
};

const CORNER_WORD: Readonly<Record<string, string>> = {
  diagonal: "diagonal",
  "blind-left": "blind",
  "blind-right": "blind",
  "lazy-susan": "lazy susan",
};

/**
 * The attributes to add to a product search for this placed object.
 * Empty when the object is not something a size meaningfully narrows.
 */
export function objectSearchAttributes(object: SearchableObject): SearchAttribute[] {
  const type = CABINET_TYPE[object.definitionId];
  if (!type) return [];
  const attributes: SearchAttribute[] = [];
  const size = widthLabel(object.width);
  if (size) attributes.push({ key: "size", value: size });

  let typeValue = type;
  if (object.definitionId === "obj-vanity" && object.config === "double") typeValue = "double sink bathroom vanity";
  if (object.definitionId === "obj-vanity" && object.config === "pedestal") typeValue = "pedestal sink";
  if ((object.definitionId === "obj-corner-base" || object.definitionId === "obj-corner-wall") && object.config) {
    const word = CORNER_WORD[cornerShapeOf(object.config)];
    if (word) typeValue = `${word} ${type}`;
  }
  attributes.push({ key: "type", value: typeValue });

  // A tall cabinet's height is a real buying decision (84 / 90 / 96);
  // a base cabinet's is not, because they are all the same.
  const cabinetClass: CabinetClass | null = cabinetClassOf(object.definitionId);
  if (cabinetClass === "tall" && object.heightInches) {
    attributes.push({ key: "height", value: `${Math.round(object.heightInches)} in` });
  }
  return attributes;
}
